// Media settings tab — two-column dense layout:
//   • Left  panel: MediaJobsLogPanel (fills column height, internal scroll)
//   • Right panel: settings rail (fills column height, internal scroll)
// Both columns lock to `100vh - HEIGHT_OFFSET_PX` on xl+ so the rail stays
// anchored while only the inner body scrolls — no double scrollbar, no
// vertical stretch when one side has more content. On <xl viewports the
// columns stack and scroll with the page.
//
// HEIGHT_OFFSET_PX accounts for the topbar + settings-shell header + tab
// strip + breathing space. Tweak in one place if shell chrome grows.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useScrollToAnchor } from "@/features/settings/hooks/use-scroll-to-anchor";
import { useCurrentOrg, useIsOrgAdmin } from "@/stores";
import { mediaAdminSettingsService } from "@/lib/api/services/media-admin-settings.service";
import { mediaKeys } from "@/hooks/api/keys/media.keys";
import { Skeleton } from "@/components/ui/skeleton";
import { RetryPolicyCard } from "@/features/settings/media/components/RetryPolicyCard";
import { MimeDenyCard } from "@/features/settings/media/components/MimeDenyCard";
import { PipelineDefaultCard } from "@/features/settings/media/components/pipeline-default-card";
import AdminMediaSettingsPage from "@/features/media/admin/AdminMediaSettingsPage";
import MediaJobsLogPanel from "@/features/transcribe/jobs/MediaJobsLogPanel";

function MediaSection() {
  useScrollToAnchor();
  const org = useCurrentOrg();
  const canEdit = useIsOrgAdmin();

  const { data, isLoading } = useQuery({
    queryKey: mediaKeys.adminSettings(org?.id ?? ""),
    queryFn: () => mediaAdminSettingsService.getSettings(),
    enabled: !!org?.id,
    staleTime: 30 * 1000,
  });

  const settings = data?.data;

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,440px)] xl:h-[calc(100vh-220px)] xl:min-h-[560px]">
      {/* Left: live media-jobs feed */}
      <div className="flex min-h-0 flex-col">
        <MediaJobsLogPanel />
      </div>

      {/* Right: settings rail — internal scroll-y body */}
      <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto pr-1">
        <AdminMediaSettingsPage
          chromeless
          settings={settings}
          isSettingsLoading={isLoading}
        />
        {isLoading || !settings ? (
          <>
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </>
        ) : (
          <>
            <RetryPolicyCard settings={settings} canEdit={canEdit} />
            <MimeDenyCard settings={settings} canEdit={canEdit} />
            <PipelineDefaultCard settings={settings} canEdit={canEdit} />
          </>
        )}
      </aside>
    </div>
  );
}

export const Route = createFileRoute("/_admin/settings/media")({
  component: MediaSection,
});
